import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import ModalConfirmacaoValorHora from './ModalConfirmacaoValorHora';
import ModalNfAdicionais from './ModalNfAdicionais';
import {
  atualizarFechamentoPj,
  calcularFechamentoPj,
  excluirFechamentoPj,
  listarHistoricoValorHoraPj,
  listarPrestadoresPj,
  obterHorasSugeridasFechamentoPj,
  salvarFechamentoPj,
} from '~/Services/Service';
import { competenciaSchema } from '~/validationSchemas';
import {
  PERCENTUAL_RETENCAO_DEFAULT,
  TIPO_REMUNERACAO_FIXO,
  TIPO_REMUNERACAO_HORA,
  formatarRemuneracao,
} from '../../Prestadores/utils/formatters';

const moeda = valor => Number(valor || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const arredondar = valor => Math.round(Number(valor || 0) * 100) / 100;

const formatarHorasInput = value => {
  if (value === null || value === undefined || value === '') return '';
  const numero = Number(value);
  if (Number.isNaN(numero)) return '';
  return Number.isInteger(numero) ? String(numero) : numero.toFixed(2);
};

const converterHorasDecimal = value => {
  const texto = String(value || '').trim().toLowerCase();
  if (!texto) return 0;

  const horasMinutos = texto.match(/^(\d{1,5}):([0-5]\d)$/);
  if (horasMinutos) {
    return Number(horasMinutos[1]) + Number(horasMinutos[2]) / 60;
  }

  const horasTexto = texto.match(/^(\d{1,5})h(?:(\d{1,2})min)?$/);
  if (horasTexto) {
    return Number(horasTexto[1]) + Number(horasTexto[2] || 0) / 60;
  }

  return Number(texto.replace(',', '.')) || 0;
};

const obterPeriodoCompetencia = competencia => {
  if (!competencia) return null;
  const partes = competencia.split('-').map(Number);
  if (partes.length < 2 || !partes[0] || !partes[1]) return null;
  const ano = partes[0];
  const mes = partes[1];
  const dataFim = new Date(ano, mes, 24);
  return {
    inicio: `${competencia}-25`,
    fim: dataFim.toISOString().slice(0, 10),
  };
};

// Cálculo local de preview enquanto a chamada ao backend está em flight.
// Replica a regra do backend: bifurca por modalidade da vigência. Em Tipo='F',
// Valor_NF = Valor_Fixo (independe de horas).
const calcularLocal = (vigencia, horasInformadas) => {
  if (!vigencia) return null;
  const ehFixo = vigencia.tipo_remuneracao === TIPO_REMUNERACAO_FIXO;
  const valorNf = ehFixo
    ? arredondar(vigencia.valor_fixo)
    : arredondar(horasInformadas * Number(vigencia.valor_hora || 0));
  const percentual13 = Number(vigencia.percentual_13 != null
    ? vigencia.percentual_13
    : PERCENTUAL_RETENCAO_DEFAULT);
  const percentual14 = Number(vigencia.percentual_14 != null
    ? vigencia.percentual_14
    : PERCENTUAL_RETENCAO_DEFAULT);
  const percentualFundo = Number(vigencia.percentual_fundo != null
    ? vigencia.percentual_fundo
    : PERCENTUAL_RETENCAO_DEFAULT);
  const valor13 = arredondar(valorNf * percentual13 / 100);
  const valor14 = arredondar(valorNf * percentual14 / 100);
  const valorFundo = arredondar(valorNf * percentualFundo / 100);
  return {
    valor_nf: valorNf,
    valor13,
    valor14,
    valor_fundo: valorFundo,
    total30: arredondar(valor13 + valor14 + valorFundo),
  };
};

const CHAVE_GES = /^\s*\d+\s*\.\s*\d+\s*\.\s*\d+\s*$/;

// Schema bifurcado: valor_hora é obrigatório só em Tipo='H'. Horas é obrigatório
// em Tipo='H' e opcional em Tipo='F' (BE assume Horas_Base como fallback).
const schema = Yup.object().shape({
  codigoPj: Yup.number()
    .required('Prestador obrigatório')
    .positive('Prestador obrigatório'),
  competencia: competenciaSchema,
  horas: Yup.string().when('tipoRemuneracao', {
    is: TIPO_REMUNERACAO_FIXO,
    then: Yup.string()
      .nullable()
      .matches(
        /^$|^(\d{1,5}(:[0-5]\d|[.,]\d{1,2})?|\d{1,5}h(\d{1,2}min)?)$/,
        'Informe horas como 7:45, 7.75 ou 7h45min'
      ),
    otherwise: Yup.string()
      .required('Horas obrigatórias')
      .matches(
        /^(\d{1,5}(:[0-5]\d|[.,]\d{1,2})?|\d{1,5}h(\d{1,2}min)?)$/,
        'Informe horas como 7:45, 7.75 ou 7h45min'
      ),
  }),
  valorHora: Yup.number().when('tipoRemuneracao', {
    is: TIPO_REMUNERACAO_HORA,
    then: Yup.number()
      .required('Valor/hora obrigatório')
      .positive('Valor/hora deve ser maior que zero'),
    otherwise: Yup.number().nullable(),
  }),
  numNf: Yup.string().max(30, 'Nº NF deve conter até 30 caracteres'),
  observacao: Yup.string().max(500, 'Observação deve conter até 500 caracteres'),
  chave: Yup.string().matches(CHAVE_GES, {
    message: 'Chave deve estar no formato GE.Portador.Sequência (ex.: 1.237.45821)',
    excludeEmptyString: true,
  }),
  nfAdicionais: Yup.array().of(
    Yup.object().shape({
      numNf: Yup.string()
        .max(50, 'NF adicional deve conter até 50 caracteres')
        .test('sem-reservados', 'NF adicional não pode conter os caracteres = ou ;',
          valor => !valor || !/[=;]/.test(valor))
        .when('chave', {
          is: chave => !!(chave || '').trim(),
          then: Yup.string().required('Informe a NF adicional'),
        }),
      chave: Yup.string()
        .matches(CHAVE_GES, {
          message: 'Chave adicional deve estar no formato GE.Portador.Sequência',
          excludeEmptyString: true,
        })
        .when('numNf', {
          is: numNf => !!(numNf || '').trim(),
          then: Yup.string().required('Informe a chave adicional'),
        }),
    }, [['numNf', 'chave']]),
  ),
});

const normalizarPreview = preview => ({
  valor_nf: preview && preview.valor_nf != null ? preview.valor_nf : 0,
  valor13: preview && preview.valor13 != null ? preview.valor13 : 0,
  valor14: preview && preview.valor14 != null ? preview.valor14 : 0,
  valor_fundo: preview && preview.valor_fundo != null ? preview.valor_fundo : 0,
  total30: preview && preview.total30 != null ? preview.total30 : 0,
});

const obterCampo = (objeto, ...nomes) => {
  const nome = nomes.find(campo => objeto && objeto[campo] != null);
  return nome ? objeto[nome] : null;
};

const criarVigenciaAPartirDoSnapshot = fechamento => {
  if (!fechamento) return null;
  const tipoRemuneracao = obterCampo(
    fechamento,
    'tipo_remuneracao_snapshot',
    'tipoRemuneracaoSnapshot'
  );
  if (!tipoRemuneracao) return null;

  return {
    tipo_remuneracao: tipoRemuneracao,
    valor_hora: obterCampo(fechamento, 'valor_hora', 'valorHora'),
    valor_fixo: obterCampo(fechamento, 'valor_fixo_snapshot', 'valorFixoSnapshot'),
    horas_base: obterCampo(fechamento, 'horas_base_snapshot', 'horasBaseSnapshot'),
    percentual_13: obterCampo(fechamento, 'percentual13_snapshot', 'percentual_13_snapshot', 'percentual13Snapshot'),
    percentual_14: obterCampo(fechamento, 'percentual14_snapshot', 'percentual_14_snapshot', 'percentual14Snapshot'),
    percentual_fundo: obterCampo(fechamento, 'percentual_fundo_snapshot', 'percentualFundoSnapshot'),
    origem: 'snapshot_fechamento',
  };
};

const ModalNovoFechamento = ({
  show,
  onHide,
  onSalvo,
  fechamento,
  podeExcluir,
  onExcluido,
}) => {
  const [prestadores, setPrestadores] = useState([]);
  const [preview, setPreview] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [carregandoHoras, setCarregandoHoras] = useState(false);
  // Vigência ATIVA na competência selecionada — drives a bifurcação H/F da UI inteira.
  const [vigenciaCompetencia, setVigenciaCompetencia] = useState(null);
  const [confirmacaoValorHora, setConfirmacaoValorHora] = useState(false);
  const [confirmacaoExcluir, setConfirmacaoExcluir] = useState(false);
  // DW-1063 (ADR-005): modal dedicado "Notas Fiscais Adicionais" — 100% local, sem
  // chamada de API. O Salvar nunca fica bloqueado esperando request de adicionais.
  const [mostrarModalAdicionais, setMostrarModalAdicionais] = useState(false);
  const proximoIdAdicionalRef = useRef(0);

  const modoEdicao = !!(fechamento && fechamento.codigo);

  const ehFixo = vigenciaCompetencia
    && vigenciaCompetencia.tipo_remuneracao === TIPO_REMUNERACAO_FIXO;

  const salvar = async (values, confirmarAtualizacaoCadastroVH) => {
    try {
      const horasDigitadas = converterHorasDecimal(values.horas);
      const payload = {
        codigo: modoEdicao ? fechamento.codigo : undefined,
        codigo_pj: Number(values.codigoPj),
        competencia: values.competencia,
        // Em Tipo='F' com horas em branco, BE faz fallback para Horas_Base.
        horas_trabalhadas: horasDigitadas > 0 ? horasDigitadas : null,
        // Em Tipo='F' o valor_hora não é enviado — BE ignora (vigência fixa é fonte do NF).
        valor_hora: ehFixo ? null : Number(values.valorHora),
        // Confirmação só faz sentido em Tipo='H' — em 'F' nem chegamos a abrir o modal.
        confirmar_atualizacao_cadastro_vh: ehFixo ? false : confirmarAtualizacaoCadastroVH,
        num_nf: values.numNf,
        observacao: values.observacao || null,
        chave: values.chave || null,
        nf_adicionais: values.nfAdicionais
          .filter(item => (item.numNf || '').trim() || (item.chave || '').trim())
          .map(item => ({ num_nf: item.numNf, chave: item.chave })),
      };
      const fechamentoSalvo = modoEdicao
        ? await atualizarFechamentoPj(payload)
        : await salvarFechamentoPj(payload);
      toast.success(modoEdicao ? 'Fechamento atualizado.' : 'Fechamento salvo.');
      if (onSalvo) await onSalvo(fechamentoSalvo);
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar fechamento.');
    }
  };

  const confirmarExclusao = async confirmou => {
    setConfirmacaoExcluir(false);
    if (!confirmou || !modoEdicao) return;

    try {
      await excluirFechamentoPj(fechamento.codigo);
      toast.success('Fechamento excluído.');
      if (onExcluido) await onExcluido(fechamento);
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir fechamento.');
    }
  };

  const formik = useFormik({
    initialValues: {
      codigoPj: '',
      competencia: '',
      horas: '',
      // Driver da bifurcação no schema do Yup. Default 'H' até a vigência ser carregada.
      tipoRemuneracao: TIPO_REMUNERACAO_HORA,
      valorHora: '',
      numNf: '',
      observacao: '',
      chave: '',
      nfAdicionais: [],
    },
    validationSchema: schema,
    onSubmit: async values => {
      // Em Tipo='F', pula completamente o fluxo de "valor_hora divergente" — não há comparação.
      if (ehFixo) {
        await salvar(values, false);
        return;
      }
      const valorInformado = Number(values.valorHora || 0);
      const valorVigente = vigenciaCompetencia && vigenciaCompetencia.valor_hora != null
        ? Number(vigenciaCompetencia.valor_hora)
        : 0;
      const valorDivergente = valorVigente > 0
        && Math.round(valorInformado * 100) !== Math.round(valorVigente * 100);

      if (valorDivergente && vigenciaCompetencia.origem !== 'snapshot_fechamento') {
        setConfirmacaoValorHora(true);
        formik.setSubmitting(false);
        return;
      }

      await salvar(values, false);
    },
  });

  useEffect(() => {
    if (!show) return;
    if (!modoEdicao) return;

    // DW-1063 (ADR-005 supersede ADR-004): observacao, chave e nf_adicionais vêm todos no
    // mesmo objeto do grid — hidratados juntos, sem chamada extra. O array já vem pronto
    // em fechamento.nf_adicionais (FechamentoListItem.NfAdicionais no backend).
    const nfAdicionaisOrigem = Array.isArray(fechamento.nf_adicionais) ? fechamento.nf_adicionais : [];
    formik.setValues({
      codigoPj: fechamento.codigo_pj || '',
      competencia: fechamento.competencia || '',
      horas: formatarHorasInput(fechamento.horas_trabalhadas),
      tipoRemuneracao: fechamento.tipo_remuneracao_snapshot || TIPO_REMUNERACAO_HORA,
      valorHora: fechamento.valor_hora != null ? Number(fechamento.valor_hora) : '',
      numNf: fechamento.num_nf || '',
      observacao: fechamento.observacao || '',
      chave: fechamento.chave || '',
      nfAdicionais: nfAdicionaisOrigem.map(item => ({
        id: proximoIdAdicionalRef.current++,
        numNf: item.num_nf || '',
        chave: item.chave_formatada || '',
      })),
    });
    setPreview(normalizarPreview(fechamento));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, modoEdicao, fechamento && fechamento.codigo]);

  useEffect(() => {
    if (!show) return;

    listarPrestadoresPj(modoEdicao ? null : { comContratoAberto: true })
      .then(resultado => {
        setPrestadores(Array.isArray(resultado) ? resultado : []);
      })
      .catch(err => toast.error(err.message || 'Erro ao carregar prestadores.'));
  }, [show, modoEdicao]);

  // Carrega a vigência da modalidade.
  // Estratégia em duas fases:
  //   1. Apenas PJ selecionado (sem competência) → usa a vigência ATIVA
  //      (Data_Fim_Vigencia IS NULL). Já bifurca a UI imediatamente,
  //      pra a label "Valor/hora" aparecer/sumir conforme o tipo correto.
  //   2. PJ + competência → re-filtra pelo mês exato (relevante quando o PJ
  //      mudou de modalidade no passado e o usuário está lançando uma
  //      competência antiga coberta por outra vigência).
  useEffect(() => {
    if (!show) {
      formik.resetForm();
      setPreview(null);
      setCarregandoHoras(false);
      setVigenciaCompetencia(null);
      setConfirmacaoValorHora(false);
      setConfirmacaoExcluir(false);
      setMostrarModalAdicionais(false);
      return;
    }
    if (!formik.values.codigoPj) {
      setVigenciaCompetencia(null);
      return;
    }

    listarHistoricoValorHoraPj(formik.values.codigoPj)
      .then(historico => {
        const lista = Array.isArray(historico) ? historico : [];
        // Vigência ATIVA do PJ — é o estado "verdadeiro" da modalidade do prestador hoje.
        // Sempre usamos como fallback para preservar a UI bifurcada mesmo quando a competência
        // selecionada não está coberta por nenhuma vigência (ex.: contrato assinado em 15/04
        // não cobre a competência de abril; ou competência anterior ao 1º contrato).
        const vigenciaAtiva = lista.find(item => !item.data_fim_vigencia) || lista[0] || null;

        let vigenciaCobreCompetencia = null;
        if (formik.values.competencia) {
          // Trata competência como RANGE operacional (25 → 24 do mês seguinte)
          // e procura vigência com INTERSECÇÃO.
          // Espelha a regra do backend em ObterVigenciaVigenteNaCompetencia.
          const periodo = obterPeriodoCompetencia(formik.values.competencia);
          if (periodo) {
            vigenciaCobreCompetencia = lista.find(item => {
              const inicio = (item.data_inicio_vigente || '').slice(0, 10);
              const fim = item.data_fim_vigencia ? String(item.data_fim_vigencia).slice(0, 10) : null;
              return inicio <= periodo.fim && (!fim || fim >= periodo.inicio);
            }) || null;
          }
        }

        // Com competência selecionada, a UI deve refletir somente a vigência
        // que cobre o range operacional daquela competência. Sem isso, o
        // fechamento aparenta calcular com a vigência ativa atual mesmo quando
        // o histórico mostra que ela ainda não valia naquele período.
        const vigenciaSnapshotEdicao = modoEdicao
          ? criarVigenciaAPartirDoSnapshot(fechamento)
          : null;
        const vigenciaParaUi = formik.values.competencia
          ? (vigenciaCobreCompetencia || vigenciaSnapshotEdicao)
          : vigenciaAtiva;
        setVigenciaCompetencia(vigenciaParaUi);

        if (modoEdicao) {
          return;
        }

        if (vigenciaParaUi && vigenciaParaUi.tipo_remuneracao === TIPO_REMUNERACAO_HORA) {
          formik.setFieldValue('tipoRemuneracao', TIPO_REMUNERACAO_HORA);
          formik.setFieldValue('valorHora', vigenciaParaUi.valor_hora != null ? Number(vigenciaParaUi.valor_hora) : '');
        } else if (vigenciaParaUi && vigenciaParaUi.tipo_remuneracao === TIPO_REMUNERACAO_FIXO) {
          formik.setFieldValue('tipoRemuneracao', TIPO_REMUNERACAO_FIXO);
          formik.setFieldValue('valorHora', '');
          // Pré-preenche horas com Horas_Base — usuário pode editar pra refletir horas reais.
          if (vigenciaParaUi.horas_base != null && !formik.values.horas) {
            const horasBase = Number(vigenciaParaUi.horas_base);
            formik.setFieldValue('horas', Number.isInteger(horasBase) ? String(horasBase) : horasBase.toFixed(2));
          }
        } else {
          // PJ sem nenhuma vigência cadastrada — submit ficará bloqueado pela validação do BE.
          formik.setFieldValue('tipoRemuneracao', TIPO_REMUNERACAO_HORA);
          formik.setFieldValue('valorHora', '');
          setPreview(null);
        }
      })
      .catch(err => toast.error(err.message || 'Erro ao carregar vigência.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.codigoPj, formik.values.competencia, show, modoEdicao]);

  // Horas sugeridas só faz sentido em Tipo='H' — em Tipo='F' o pre-preenchimento vem da vigência.
  useEffect(() => {
    if (!show || !formik.values.codigoPj || !formik.values.competencia) return;
    if (modoEdicao) return;
    if (ehFixo) return;

    let ativo = true;
    setCarregandoHoras(true);
    obterHorasSugeridasFechamentoPj(formik.values.codigoPj, formik.values.competencia)
      .then(resultado => {
        if (!ativo) return;
        const horas = Number(resultado && resultado.horas_trabalhadas != null
          ? resultado.horas_trabalhadas
          : 0);
        formik.setFieldValue('horas', horas.toFixed(2));
      })
      .catch(err => {
        if (ativo) toast.error(err.message || 'Erro ao carregar horas trabalhadas.');
      })
      .finally(() => {
        if (ativo) setCarregandoHoras(false);
      });

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.codigoPj, formik.values.competencia, show, ehFixo, modoEdicao]);

  // Preview em tempo real com debounce 250ms.
  // Em Tipo='F', NF é constante (= Valor_Fixo) e não muda ao alterar horas — apenas
  // re-disparamos o cálculo se a vigência ou a flag de modalidade mudar.
  useEffect(() => {
    if (!show) return;

    const horasDecimal = converterHorasDecimal(formik.values.horas);
    if (!vigenciaCompetencia) {
      setPreview(null);
      return;
    }
    if (!ehFixo && (!horasDecimal || !formik.values.valorHora)) {
      setPreview(null);
      return;
    }

    const valorHoraInformado = Number(formik.values.valorHora || 0);
    const valorHoraVigente = vigenciaCompetencia && vigenciaCompetencia.valor_hora != null
      ? Number(vigenciaCompetencia.valor_hora)
      : 0;
    const valorHoraDivergente = !ehFixo && valorHoraInformado > 0
      && Math.round(valorHoraInformado * 100) !== Math.round(valorHoraVigente * 100);
    const usandoSnapshotFechamento = vigenciaCompetencia.origem === 'snapshot_fechamento';
    const vigenciaPreview = !ehFixo
      ? { ...vigenciaCompetencia, valor_hora: valorHoraInformado }
      : vigenciaCompetencia;
    const previewLocal = calcularLocal(vigenciaPreview, horasDecimal);
    setPreview(previewLocal);

    if (valorHoraDivergente || usandoSnapshotFechamento) {
      setCalculando(false);
      return;
    }

    setCalculando(true);

    const timer = setTimeout(() => {
      calcularFechamentoPj({
        codigo_pj: Number(formik.values.codigoPj),
        competencia: formik.values.competencia,
        horas_trabalhadas: horasDecimal > 0 ? horasDecimal : null,
      })
        .then(resultado => setPreview(normalizarPreview(resultado)))
        .catch(() => setPreview(previewLocal))
        .finally(() => setCalculando(false));
    }, 250);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.horas, formik.values.valorHora, formik.values.codigoPj, formik.values.competencia, show, vigenciaCompetencia, ehFixo]);

  const prestadorOptions = useMemo(() => {
    const options = prestadores.map(prestador => ({
      value: prestador.codigo,
      label: prestador.nome_fantasia || prestador.razao_social,
      razao_social: prestador.razao_social,
    }));

    if (
      modoEdicao
      && fechamento
      && fechamento.codigo_pj
      && !options.some(option => option.value === Number(fechamento.codigo_pj))
    ) {
      options.push({
        value: Number(fechamento.codigo_pj),
        label: fechamento.razao_social || `PJ ${fechamento.codigo_pj}`,
        razao_social: fechamento.razao_social,
      });
    }

    return options;
  }, [prestadores, modoEdicao, fechamento]);

  const prestadorSelecionado = prestadorOptions.find(
    option => option.value === Number(formik.values.codigoPj)
  ) || null;

  const alterarPrestador = option => {
    formik.setFieldValue('codigoPj', option ? option.value : '');
    formik.setFieldValue('valorHora', '');
    formik.setFieldValue('horas', '');
    setVigenciaCompetencia(null);
    setPreview(null);
  };

  const erroCampo = name => (
    formik.touched[name] && formik.errors[name] ? (
      <div className="hpj-field-error">{formik.errors[name]}</div>
    ) : null
  );

  // DW-1063 (ADR-005): callbacks passados ao modal dedicado ModalNfAdicionais — cada um só
  // altera formik.values.nfAdicionais (estado do pai); nenhum chama API. `id` local estável
  // via contador em useRef (nunca `key={index}` — FE-001).
  const onAdicionarNf = ({ numNf, chave }) => {
    formik.setFieldValue('nfAdicionais', [
      ...formik.values.nfAdicionais,
      { id: proximoIdAdicionalRef.current++, numNf, chave },
    ]);
  };

  const onEditarNf = (id, { numNf, chave }) => {
    formik.setFieldValue(
      'nfAdicionais',
      formik.values.nfAdicionais.map(item => (item.id === id ? { ...item, numNf, chave } : item))
    );
  };

  const onRemoverNf = id => {
    formik.setFieldValue(
      'nfAdicionais',
      formik.values.nfAdicionais.filter(item => item.id !== id)
    );
  };

  const remuneracaoTexto = vigenciaCompetencia
    ? formatarRemuneracao(
      vigenciaCompetencia.tipo_remuneracao,
      vigenciaCompetencia.valor_hora,
      vigenciaCompetencia.valor_fixo,
      vigenciaCompetencia.horas_base,
    )
    : null;
  const semVigenciaNaCompetencia = !!(
    show
    && formik.values.codigoPj
    && formik.values.competencia
    && !vigenciaCompetencia
    && !modoEdicao
  );

  return (
    <>
      <Modal
        className={`hpj-modal hpj-modal-fechamento${confirmacaoValorHora || mostrarModalAdicionais ? ' hpj-modal-stacked-back' : ''}`}
        show={show}
        onHide={onHide}
        size="lg"
        centered
      >
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <div>
              <div className="hpj-modal-eyebrow">Fechamento mensal</div>
              <Modal.Title>{modoEdicao ? 'Editar Fechamento' : 'Novo Fechamento'}</Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body>
            {ehFixo && remuneracaoTexto && (
              <Alert variant="success" className="mb-3">
                <strong>🟢 Valor Fixo</strong> — {remuneracaoTexto}. Valor NF é fixo, não depende das horas trabalhadas.
              </Alert>
            )}
            {semVigenciaNaCompetencia && (
              <Alert variant="warning" className="mb-3">
                Não há vigência de remuneração cobrindo a competência selecionada. Ajuste o histórico do prestador antes de lançar o fechamento.
              </Alert>
            )}
            <Row>
              <Col md={4}>
                <Form.Group className={formik.touched.codigoPj && formik.errors.codigoPj ? 'has-error' : ''}>
                  <Form.Label>
                    Prestador
                    <span className="hpj-required">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="hpj-user-select"
                    isClearable
                    isDisabled={modoEdicao}
                    options={prestadorOptions}
                    placeholder="Selecione"
                    value={prestadorSelecionado}
                    onBlur={() => formik.setFieldTouched('codigoPj', true)}
                    onChange={alterarPrestador}
                  />
                  {erroCampo('codigoPj')}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className={formik.touched.competencia && formik.errors.competencia ? 'has-error' : ''}>
                  <Form.Label>
                    Competência
                    <span className="hpj-required">*</span>
                  </Form.Label>
                  <Form.Control
                    name="competencia"
                    type="month"
                    value={formik.values.competencia}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    disabled={modoEdicao}
                  />
                  {erroCampo('competencia')}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className={formik.touched.horas && formik.errors.horas ? 'has-error' : ''}>
                  <Form.Label>
                    Horas trabalhadas
                    {!ehFixo && <span className="hpj-required">*</span>}
                  </Form.Label>
                  <Form.Control
                    name="horas"
                    placeholder={carregandoHoras ? 'Calculando...' : '7:45'}
                    value={formik.values.horas}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    disabled={carregandoHoras}
                  />
                  {erroCampo('horas')}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {!ehFixo ? (
                <Col md={4}>
                  <Form.Group className={formik.touched.valorHora && formik.errors.valorHora ? 'has-error' : ''}>
                    <Form.Label>
                      Valor/hora
                      <span className="hpj-required">*</span>
                    </Form.Label>
                    <Form.Control
                      name="valorHora"
                      min="0"
                      step="0.01"
                      type="number"
                      value={formik.values.valorHora}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    />
                    {erroCampo('valorHora')}
                  </Form.Group>
                </Col>
              ) : (
                /* ADR-013: em Tipo='F', exibe Valor Hora readonly = Valor_Fixo / Horas_Trabalhadas
                   (a "hora efetiva do mês"). Recalcula em runtime conforme Horas mudam. */
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Valor/hora</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={(() => {
                        const horasDecimal = converterHorasDecimal(formik.values.horas);
                        const horasParaCalc = horasDecimal > 0
                          ? horasDecimal
                          : (vigenciaCompetencia && vigenciaCompetencia.horas_base ? Number(vigenciaCompetencia.horas_base) : 0);
                        const valorFixo = vigenciaCompetencia ? Number(vigenciaCompetencia.valor_fixo || 0) : 0;
                        return horasParaCalc > 0 && valorFixo > 0
                          ? (valorFixo / horasParaCalc).toFixed(2)
                          : '';
                      })()}
                      placeholder="—"
                      title="Calculado: Valor fixo ÷ Horas trabalhadas"
                      style={{ background: '#f5f5f5' }}
                    />
                    <Form.Text className="text-muted">
                      Hora efetiva do mês: Valor fixo ÷ Horas trabalhadas
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Nº NF</Form.Label>
                  <Form.Control
                    name="numNf"
                    value={formik.values.numNf}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {erroCampo('numNf')}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className={formik.touched.chave && formik.errors.chave ? 'has-error' : ''}>
                  <Form.Label>Chave NF</Form.Label>
                  <Form.Control
                    name="chave"
                    placeholder="1.237.45821"
                    value={formik.values.chave}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {erroCampo('chave')}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Observação</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="observacao"
                    maxLength={500}
                    value={formik.values.observacao}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {erroCampo('observacao')}
                </Form.Group>
              </Col>
            </Row>

            <div className="hpj-form-section">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="hpj-form-section-title mb-0">NF Adicionais</div>
                <Button
                  className="hpj-btn"
                  size="sm"
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setMostrarModalAdicionais(true)}
                >
                  NFs Adicionais
                </Button>
              </div>
              {formik.values.nfAdicionais.length > 0 && (
                <div className="text-muted">
                  {formik.values.nfAdicionais.length} NF adicional(is) informada(s).
                </div>
              )}
            </div>

            {preview && (
              <div className="hpj-preview-card">
                <div className="hpj-preview-item">
                  <span>Valor NF</span>
                  <strong>{moeda(preview.valor_nf)}</strong>
                </div>
                <div className="hpj-preview-item">
                  <span>Total 30%</span>
                  <strong>{moeda(preview.total30)}</strong>
                </div>
                <div className="hpj-preview-item">
                  <span>13ª Nota</span>
                  <strong>{moeda(preview.valor13)}</strong>
                </div>
                <div className="hpj-preview-item">
                  <span>14ª Nota</span>
                  <strong>{moeda(preview.valor14)}</strong>
                </div>
                <div className="hpj-preview-item">
                  <span>Fundo</span>
                  <strong>{moeda(preview.valor_fundo)}</strong>
                </div>
                <div className="hpj-preview-item">
                  <span>Status</span>
                  <strong>{calculando ? 'Calculando...' : 'Preview'}</strong>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {modoEdicao && podeExcluir && (
              <Button
                className="hpj-btn hpj-btn-danger mr-auto"
                variant="outline-danger"
                type="button"
                onClick={() => setConfirmacaoExcluir(true)}
              >
                Excluir
              </Button>
            )}
            <Button className="hpj-btn" variant="outline-secondary" onClick={onHide}>
              Cancelar
            </Button>
            <Button className="hpj-btn hpj-btn-primary" type="submit" disabled={formik.isSubmitting || carregandoHoras || semVigenciaNaCompetencia}>
              {modoEdicao ? 'Salvar Alterações' : 'Salvar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ModalConfirmacaoValorHora
        show={confirmacaoValorHora}
        valorInformado={Number(formik.values.valorHora || 0)}
        valorVigente={vigenciaCompetencia && vigenciaCompetencia.valor_hora != null
          ? Number(vigenciaCompetencia.valor_hora)
          : 0}
        onSelecionar={acao => {
          setConfirmacaoValorHora(false);
          if (acao === 'lancar') {
            salvar(formik.values, false);
          } else if (acao === 'alterar') {
            salvar(formik.values, true);
          }
          // 'fechar' -> não salva; volta ao formulário para ajuste
        }}
      />
      <ModalConfirmacao
        show={confirmacaoExcluir}
        title="Excluir fechamento?"
        message="Esta ação remove o fechamento e as movimentações de fundo vinculadas. Confirme apenas se deseja excluir definitivamente este registro."
        variant="danger"
        onClose={confirmarExclusao}
      />
      <ModalNfAdicionais
        show={mostrarModalAdicionais}
        onHide={() => setMostrarModalAdicionais(false)}
        itens={formik.values.nfAdicionais}
        onAdicionar={onAdicionarNf}
        onEditar={onEditarNf}
        onRemover={onRemoverNf}
      />
    </>
  );
};

export default ModalNovoFechamento;
